import { useTheme } from '../../contexts/useTheme';
import { Package, Clock, AlertTriangle, TrendingUp, Calendar, Shield } from 'lucide-react';
import type { DriftReport, MaintenanceScoreResult } from '../types/analysis.types';
import { MaintenanceScoreSection } from './maintenance';

export type ScoreTab = 'sdlc' | 'drift';

interface ScoreTabsPanelProps {
  maintenanceScore?: MaintenanceScoreResult | null;
  driftData?: DriftReport | null;
  activeTab: ScoreTab;
  className?: string;
}

export const ScoreTabsPanel: React.FC<ScoreTabsPanelProps> = ({
  maintenanceScore,
  driftData,
  activeTab,
  className = '',
}) => {
  const { theme } = useTheme();
  const hasMaintenanceScore = !!maintenanceScore;
  const cardBg = theme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.9)';
  const cardBorder = theme === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(203, 213, 225, 0.6)';
  const trackColor = theme === 'dark' ? 'rgba(51, 65, 85, 0.4)' : 'rgba(226, 232, 240, 0.8)';
  const metricBg = theme === 'dark' ? 'rgba(51, 65, 85, 0.3)' : 'rgba(241, 245, 249, 0.8)';

  return (
    <div className={'max-w-7xl mx-auto ' + className}>
      {activeTab === 'drift' && hasMaintenanceScore && (
        <MaintenanceScoreSection maintenanceScore={maintenanceScore!} driftData={driftData} />
      )}

      {activeTab === 'drift' && !hasMaintenanceScore && driftData && (
        <LegacyDriftPanel
          driftData={driftData}
          cardBg={cardBg}
          cardBorder={cardBorder}
          metricBg={metricBg}
          trackColor={trackColor}
          theme={theme}
        />
      )}

      {activeTab === 'drift' && !hasMaintenanceScore && !driftData && (
        <div
          className="rounded-2xl p-6 md:p-8 text-center"
          style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
        >
          <Package className="w-12 h-12 mx-auto mb-3 text-secondary" />
          <h3 className="text-lg font-semibold text-primary mb-1">No Drift Data</h3>
          <p className="text-sm text-secondary">
            Run a full repository analysis first to generate dependency drift data.
          </p>
        </div>
      )}
    </div>
  );
};

// Helper to format age in years to months/years
function formatAge(years: number): { value: string; unit: string } {
  if (years < 1) {
    const months = Math.round(years * 12);
    return { value: String(months), unit: months === 1 ? 'month' : 'months' };
  }
  // Show precise decimal for 1+ years (e.g., "1.5 years", "2.3 years")
  return { value: years.toFixed(1), unit: 'years' };
}

// Legacy drift panel for backward compatibility
interface LegacyDriftPanelProps {
  driftData: DriftReport;
  cardBg: string;
  cardBorder: string;
  metricBg: string;
  trackColor: string;
  theme: string;
}

const LegacyDriftPanel: React.FC<LegacyDriftPanelProps> = ({
  driftData,
  cardBg,
  cardBorder,
  metricBg,
  trackColor,
  theme,
}) => {
  const driftPct = Math.round(driftData.metrics.driftRatio * 100);
  const oldestAge = formatAge(driftData.metrics.oldestPinnedYears);

  return (
    <div
      className="rounded-2xl p-6 md:p-8"
      style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}` }}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-primary flex items-center gap-2">
              <Package className="w-6 h-6 text-teal-500" />
              Dependency Drift Analysis
            </h3>
            <p className="text-sm text-secondary mt-1">{driftData.interpretation.summary}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-lg p-4" style={{ backgroundColor: metricBg }}>
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-secondary" />
              <span className="text-xs text-secondary uppercase">Total</span>
            </div>
            <div className="text-2xl font-bold text-primary">
              {driftData.metrics.totalDependencies}
            </div>
            <div className="text-xs text-secondary">dependencies</div>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: metricBg }}>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-secondary uppercase">Outdated</span>
            </div>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {driftData.metrics.outdatedDependencies}
            </div>
            <div className="text-xs text-secondary">{driftPct}% of total</div>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: metricBg }}>
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-secondary" />
              <span className="text-xs text-secondary uppercase">&gt;2 Years</span>
            </div>
            <div className="text-2xl font-bold text-primary">
              {Math.round(driftData.metrics.shareOlderThan2Years * 100)}%
            </div>
            <div className="text-xs text-secondary">of dependencies</div>
          </div>
          <div className="rounded-lg p-4" style={{ backgroundColor: metricBg }}>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-secondary" />
              <span className="text-xs text-secondary uppercase">Oldest</span>
            </div>
            <div className="text-2xl font-bold text-primary">{oldestAge.value}</div>
            <div className="text-xs text-secondary">{oldestAge.unit} old</div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Age Distribution
          </h4>
          <div className="space-y-2">
            {driftData.ageDistribution.map((ageGroup, idx) => {
              const pct =
                driftData.metrics.totalDependencies > 0
                  ? Math.round((ageGroup.count / driftData.metrics.totalDependencies) * 100)
                  : 0;
              const barColors = ['#ef4444', '#f97316', '#eab308', '#10b981'];
              const bucketLabels: Record<string, string> = {
                '7+': '7+ years',
                '4-6': '4-6 years',
                '2-4': '2-4 years',
                '<2': '<2 years',
              };
              return (
                <div key={ageGroup.bucket} className="flex items-center gap-3">
                  <div className="w-24 text-xs text-secondary">
                    {bucketLabels[ageGroup.bucket] || ageGroup.bucket}
                  </div>
                  <div
                    className="flex-1 h-6 rounded-full overflow-hidden"
                    style={{ backgroundColor: trackColor }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: barColors[idx % barColors.length],
                      }}
                    />
                  </div>
                  <div className="w-16 text-right">
                    <span className="text-sm font-medium text-primary">{ageGroup.count}</span>
                    <span className="text-xs text-secondary ml-1">({pct}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {driftData.staleDependencies.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              Stale Dependencies (Needs Attention)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {driftData.staleDependencies.slice(0, 6).map((dep) => {
                const depAge = formatAge(dep.ageYears);
                return (
                  <div
                    key={dep.name}
                    className="flex items-center justify-between p-3 rounded-lg"
                    style={{ backgroundColor: metricBg }}
                  >
                    <div>
                      <span className="text-sm font-medium text-primary">{dep.name}</span>
                      <span className="text-xs text-secondary ml-2">
                        {dep.pinned} → {dep.latest}
                      </span>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                      {depAge.value} {depAge.unit} old
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Risk Implications
          </h4>
          <div
            className="rounded-lg p-4 text-sm"
            style={{
              backgroundColor:
                theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(254, 226, 226, 0.5)',
              border: `1px solid ${theme === 'dark' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.4)'}`,
            }}
          >
            <p className="text-red-800 dark:text-red-200/90">
              {driftData.interpretation.riskImplications}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreTabsPanel;
